type DisplayCallStatus =
  | 'pending'
  | 'recording'
  | 'transcribing'
  | 'awaiting_summary'
  | 'summarizing'
  | 'complete'
  | 'failed'

type RetryTargetStatus = 'pending' | 'awaiting_summary'

export interface CallDisplayCandidate {
  status: DisplayCallStatus
  duration_seconds: number | null
  summary: string | null
  transcript: string | null
  error_message: string | null
  audio_storage_path: string | null
}

export interface CallDisplayModel {
  statusLabel: string
  statusGuidance: string
  durationLabel: string | null
  previewText: string
  retryTarget: RetryTargetStatus | null
  retryHint: string | null
}

export interface TranscriptSearchSegment {
  text: string
  matchIndex: number | null
  active: boolean
}

export interface TranscriptSearchModel {
  query: string
  totalMatches: number
  activeIndex: number
  segments: TranscriptSearchSegment[]
}

const STATUS_LABEL: Record<DisplayCallStatus, string> = {
  pending: '待機中',
  recording: '録音中',
  transcribing: '文字起こし中',
  awaiting_summary: '議事録生成待ち',
  summarizing: '議事録生成中',
  complete: '完了',
  failed: '失敗'
}

export function buildCallDisplayModel(call: CallDisplayCandidate): CallDisplayModel {
  const retryTarget = getDisplayRetryTarget(call)
  return {
    statusLabel: STATUS_LABEL[call.status],
    statusGuidance: getCallStatusGuidance(call.status),
    durationLabel: formatCallDuration(call.duration_seconds),
    previewText: buildPreviewText(call, retryTarget),
    retryTarget,
    retryHint: retryTarget ? retryHintForTarget(retryTarget) : null
  }
}

export function getCallStatusGuidance(status: DisplayCallStatus): string {
  switch (status) {
    case 'pending':
      return '処理待ちです。しばらく待っても進まない場合は再読み込みしてください。'
    case 'recording':
      return '録音中です。録音終了後に文字起こしと議事録生成へ進みます。'
    case 'transcribing':
      return '文字起こし中です。完了後に議事録とTODOが自動生成されます。'
    case 'awaiting_summary':
      return '文字起こしは完了しています。議事録生成の開始を待っています。'
    case 'summarizing':
      return '議事録とTODOを生成中です。完了までこのままお待ちください。'
    case 'complete':
      return '議事録・TODO・transcriptを確認できます。必要に応じてTODOと次回フォローを更新してください。'
    case 'failed':
      return '処理に失敗しました。失敗理由を確認し、再試行できる場合は再試行してください。'
  }
}

export function buildTranscriptSearchModel(
  transcript: string,
  rawQuery: string,
  activeSeed = 0
): TranscriptSearchModel {
  const query = rawQuery.trim()
  if (!query) {
    return {
      query: '',
      totalMatches: 0,
      activeIndex: -1,
      segments: [{ text: transcript, matchIndex: null, active: false }]
    }
  }

  const lowerTranscript = transcript.toLocaleLowerCase()
  const lowerQuery = query.toLocaleLowerCase()
  const matches: Array<{ start: number; end: number }> = []
  let cursor = 0
  while (cursor <= transcript.length) {
    const start = lowerTranscript.indexOf(lowerQuery, cursor)
    if (start === -1) break
    const end = start + query.length
    matches.push({ start, end })
    cursor = end > start ? end : start + 1
  }

  if (matches.length === 0) {
    return {
      query,
      totalMatches: 0,
      activeIndex: -1,
      segments: [{ text: transcript, matchIndex: null, active: false }]
    }
  }

  const activeIndex = normalizeActiveIndex(activeSeed, matches.length)
  const segments: TranscriptSearchSegment[] = []
  let lastEnd = 0
  matches.forEach((match, matchIndex) => {
    if (match.start > lastEnd) {
      segments.push({ text: transcript.slice(lastEnd, match.start), matchIndex: null, active: false })
    }
    segments.push({
      text: transcript.slice(match.start, match.end),
      matchIndex,
      active: matchIndex === activeIndex
    })
    lastEnd = match.end
  })
  if (lastEnd < transcript.length) {
    segments.push({ text: transcript.slice(lastEnd), matchIndex: null, active: false })
  }

  return { query, totalMatches: matches.length, activeIndex, segments }
}

function getDisplayRetryTarget(call: CallDisplayCandidate): RetryTargetStatus | null {
  if (call.status !== 'failed') return null
  if (call.audio_storage_path?.trim()) return 'pending'
  if (call.transcript && call.transcript.trim().length >= 20) return 'awaiting_summary'
  return null
}

export function formatCallDuration(seconds: number | null): string | null {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds <= 0) return null
  if (seconds < 60) return `${seconds}秒`
  const minutes = Math.floor(seconds / 60)
  const rest = seconds % 60
  return rest === 0 ? `${minutes}分` : `${minutes}分${rest}秒`
}

export function firstSummaryLine(summary: string): string {
  for (const line of summary.split('\n')) {
    const text = line.trim()
    if (!text) continue
    if (text.startsWith('## ') || text.startsWith('# ')) continue
    return truncate(text)
  }
  return truncate(summary)
}

function buildPreviewText(
  call: CallDisplayCandidate,
  retryTarget: RetryTargetStatus | null
): string {
  if (call.status === 'failed') {
    if (call.error_message?.trim()) return call.error_message.trim()
    if (retryTarget === 'awaiting_summary') return '保存済みの文字起こしから再生成できます。'
    if (retryTarget === 'pending') return '保存済み音声から再処理できます。'
    return '通話処理に失敗しました。'
  }
  if (call.summary?.trim()) return firstSummaryLine(call.summary)
  if (call.transcript?.trim()) {
    return `議事録生成中… (transcript ${call.transcript.length} 文字)`
  }
  return '処理中…'
}

function retryHintForTarget(target: RetryTargetStatus): string {
  return target === 'pending'
    ? '音声ファイルから文字起こしをやり直します'
    : '保存済み文字起こしから議事録生成をやり直します'
}

function truncate(text: string): string {
  return text.length > 200 ? `${text.slice(0, 200)}…` : text
}

function normalizeActiveIndex(seed: number, total: number): number {
  if (total <= 0) return -1
  const rounded = Math.trunc(seed)
  return ((rounded % total) + total) % total
}
